import { getQueryStringParams, setQueryStringParam } from "../../shared/utils";

export function aggregationDropdown(args) {
  return {
    expanded: false,
    aggregationOptions: [],
    tagGroups: args?.tagGroups || [],
    customSections: args?.customSections || [],
    enableIndividualMetrics: args?.enableIndividualMetrics || false,
    enableTagGroupMetrics: args?.enableTagGroupMetrics !== false, // default true
    hideCodingActivity: args?.hideCodingActivity || false,
    defaultValue: args?.defaultValue || null,
    onChange: args?.onChange || null,
    loading: false,
    currentAggregation: null, // Local reactive property for current value

    init() {
      this.buildAggregationOptions();
      this.setInitialValue();

      // Set initial current aggregation
      this.currentAggregation = this.getAggregationValue();

      // Watch for changes from other components
      this.$watch("$store.snapshotResultFilters.aggregation", (value) => {
        // Update local reactive property
        this.currentAggregation = value;

        if (value && this.onChange) {
          const selection = this.aggregationOptions.find(
            (o) => o.value === value,
          );
          if (selection) {
            if (typeof this.onChange === "function") {
              this.onChange(selection);
            } else if (typeof this.onChange === "string") {
              // Try to find the method in parent components
              if (
                this.$parent &&
                typeof this.$parent[this.onChange] === "function"
              ) {
                this.$parent[this.onChange]();
              } else if (
                this.$root &&
                typeof this.$root[this.onChange] === "function"
              ) {
                this.$root[this.onChange]();
              }
            }
          }
        }
      });
    },

    getAggregationValue() {
      const params = getQueryStringParams();
      return (
        params.get("aggregation") ||
        this.$store.snapshotResultFilters?.aggregation ||
        this.defaultValue ||
        "team"
      );
    },

    get aggregation() {
      // Use local reactive property if set, otherwise get from URL/store
      return this.currentAggregation || this.getAggregationValue();
    },

    get selection() {
      return this.aggregationOptions.find((o) => o.value === this.aggregation);
    },

    get selectionLabel() {
      if (this.loading) return "";
      return this.selection?.label || "Team";
    },

    get teamOptions() {
      return this.aggregationOptions.filter((o) => o.type === "people");
    },

    get tagOptions() {
      return this.aggregationOptions.filter((o) => o.type === "tag");
    },

    get customOptions() {
      return this.aggregationOptions.filter((o) => o.type === "custom");
    },

    buildAggregationOptions() {
      let options = [];

      // Add custom sections if provided
      if (this.customSections?.length) {
        this.customSections.forEach((section) => {
          section.options.forEach((opt) => {
            options.push({
              label: opt.label,
              type: "custom",
              value: opt.value,
              sectionLabel: section.sectionLabel,
            });
          });
        });
      }

      // Add team aggregation options (always use new pattern)
      options.push({
        label: "Team tree",
        type: "people",
        value: "team",
      });
      options.push({
        label: "Leaf teams",
        type: "people",
        value: "leaf_teams",
      });

      // Add individual metrics if enabled
      if (this.enableIndividualMetrics) {
        options.push({
          label: "Contributor",
          type: "people",
          value: "contributor",
        });
      }

      // Add tag groups if enabled
      if (this.tagGroups?.length > 0 && this.enableTagGroupMetrics) {
        this.tagGroups.forEach((tagGroup) => {
          if (
            this.hideCodingActivity &&
            tagGroup.name === "Coding activity level"
          ) {
            return;
          }

          options.push({
            label: tagGroup.name,
            type: "tag",
            value: `attribute_${tagGroup.encoded_id}`,
          });
        });
      }

      this.aggregationOptions = options;
    },

    setInitialValue() {
      const params = getQueryStringParams();
      let aggregation =
        params.get("aggregation") ||
        this.$store.snapshotResultFilters?.aggregation ||
        this.defaultValue ||
        "team";

      this.updateSelection(aggregation, false);
    },

    updateSelection(value, triggerChange = true) {
      // Update local reactive property immediately for UI responsiveness
      this.currentAggregation = value;

      // Update store if available (this will also update query string)
      if (this.$store?.snapshotResultFilters?.setAggregation) {
        this.$store.snapshotResultFilters.setAggregation(value);
      } else {
        // Fallback to direct query string update
        setQueryStringParam("aggregation", value);
      }

      // Trigger onChange callback
      if (triggerChange && this.onChange) {
        const selection = this.aggregationOptions.find(
          (o) => o.value === value,
        );
        if (selection) {
          // Set fetching state for loading indicators
          if (this.$parent?.isFetching !== undefined) {
            this.$parent.isFetching = true;
          }

          if (typeof this.onChange === "function") {
            this.onChange(selection);
          } else if (typeof this.onChange === "string") {
            // Use Alpine.js event system for clean cross-component communication
            this.$dispatch("aggregation-changed", {
              value: value,
              selection: selection,
              methodName: this.onChange,
            });
          }
        }
      }

      this.expanded = false;
    },

    isSelected(option) {
      return this.aggregation === option.value;
    },

    selectOption(option) {
      // Close first, then defer selection until after DOM updates
      this.expanded = false;
      this.$nextTick(() => {
        setTimeout(() => this.updateSelection(option.value), 0);
      });
    },

    toggleDropdown() {
      this.expanded = !this.expanded;
    },

    // Method to fetch data if needed (can be called from parent)
    async fetchTagGroups() {
      if (this.tagGroups?.length > 0) return;

      try {
        this.loading = true;
        const response = await fetch("/attributes/tag_groups");
        const data = await response.json();
        this.tagGroups = data.data || [];
        this.buildAggregationOptions();
      } catch (error) {
        console.error("Failed to fetch tag groups:", error);
      } finally {
        this.loading = false;
      }
    },

    // Method to reset aggregation to default "team" when navigating between breakdowns
    resetToDefaultAggregation() {
      this.updateSelection("team", true);
    },
  };
}
